import React from 'react';
import { Calendar, Flag, History, ListChecks } from 'lucide-react';
import { format, differenceInHours } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const priorityConfig = {
  low: { label: 'Baixa', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Média', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'Alta', color: 'bg-amber-100 text-amber-700' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700' }
};

const avatarColors = {
  blue: 'from-blue-500 to-cyan-500',
  purple: 'from-purple-500 to-pink-500',
  green: 'from-emerald-500 to-teal-500',
  orange: 'from-orange-500 to-amber-500',
  red: 'from-red-500 to-rose-500',
  indigo: 'from-indigo-500 to-blue-500'
};

export default function TaskCard({
  task,
  onClick,
  onHistoryClick,
  members = []
}: {
  task: any;
  onClick: (task: any) => void;
  onHistoryClick: (task: any) => void;
  members?: any[];
}) {
  const priority = priorityConfig[task.priority] || priorityConfig.medium;
  const isOverdue =
    task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';

  // Get members assigned to this task
  const assignedMembers = members.filter(m => task.assignees?.includes(m.id));

  // Calculate subtasks progress
  const subtasksTotal = task.subtasks?.length || 0;
  const subtasksCompleted = task.subtasks?.filter((st: any) => st.completed).length || 0;

  // Calculate time progress
  let timeStatus: 'overtime' | 'warning' | 'good' | null = null;

  if (
    task.estimated_hours &&
    task.started_at &&
    (task.status === 'in_progress' || task.status === 'review')
  ) {
    const elapsedHours = differenceInHours(new Date(), new Date(task.started_at));
    const percentageUsed = (elapsedHours / task.estimated_hours) * 100;

    if (percentageUsed > 100) timeStatus = 'overtime';
    else if (percentageUsed > 80) timeStatus = 'warning';
    else timeStatus = 'good';
  }

  return (
    <div
      onClick={() => onClick(task)}
      className={`
        bg-white rounded-xl p-4 mb-3
        border-2 border-slate-100
        transition-shadow duration-200
        hover:border-blue-400 hover:shadow-lg
        cursor-pointer
      `}
    >
      {/* Tags & Indicators */}
      <div className="flex flex-wrap gap-1.5 mb-3 items-center">
        {task.tags && task.tags.length > 0 && (
          <>
            {task.tags.slice(0, 2).map((tag: string, i: number) => (
              <span
                key={i}
                className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 shadow-sm"
              >
                {tag}
              </span>
            ))}
            {task.tags.length > 2 && (
              <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-slate-100 to-slate-200 text-slate-600">
                +{task.tags.length - 2}
              </span>
            )}
          </>
        )}

        {subtasksTotal > 0 && (
          <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 shadow-sm flex items-center gap-1">
            <ListChecks className="w-3 h-3" />
            {subtasksCompleted}/{subtasksTotal}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="font-semibold text-slate-900 text-sm leading-snug mb-3 line-clamp-2">
        {task.title}
      </h3>

      {/* Description Preview */}
      {task.description && (
        <p className="text-xs text-slate-500 mb-3 line-clamp-2">
          {task.description}
        </p>
      )}

      {/* Time Progress Bar */}
      {timeStatus && (
        <div className="mb-3">
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                timeStatus === 'overtime'
                  ? 'bg-gradient-to-r from-red-500 to-rose-500'
                  : timeStatus === 'warning'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500'
              }`}
              style={{
                width: `${Math.min(
                  (differenceInHours(new Date(), new Date(task.started_at)) /
                    task.estimated_hours) *
                    100,
                  100
                )}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
        <div className="flex items-center gap-2">
          {/* Priority */}
          <span
            className={`text-xs font-semibold px-2 py-1 rounded-md flex items-center ${priority.color} shadow-sm`}
          >
            <Flag className="w-3 h-3 mr-1" />
            {priority.label}
          </span>

          {/* History Button */}
          <button
            onClick={e => {
              e.stopPropagation();
              onHistoryClick(task);
            }}
            className="h-7 px-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          >
            <History className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Due Date */}
          {task.due_date && (
            <span
              className={`flex items-center text-xs font-medium px-2 py-1 rounded-lg ${
                isOverdue ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
              }`}
            >
              <Calendar className="w-3 h-3 mr-1" />
              {format(new Date(task.due_date), 'dd MMM', { locale: ptBR })}
            </span>
          )}

          {/* Assignees */}
          {assignedMembers.length > 0 && (
            <div className="flex -space-x-2">
              {assignedMembers.slice(0, 3).map((member: any, idx: number) => {
                const colorGradient =
                  avatarColors[member.avatar_color] || avatarColors.blue;

                return (
                  <div
                    key={member.id}
                    className={`w-7 h-7 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center shadow-md ring-2 ring-white`}
                    style={{ zIndex: 3 - idx }}
                    title={member.name}
                  >
                    <span className="text-xs font-bold text-white">
                      {member.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                );
              })}

              {assignedMembers.length > 3 && (
                <div
                  className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center shadow-md ring-2 ring-white text-xs font-bold text-white"
                  title={`+${assignedMembers.length - 3} membros`}
                >
                  +{assignedMembers.length - 3}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
